import React from "react";
import { BusinessHoursView } from "./BusinessHoursView";
import { getEmptyDays } from "@/utils/qrEditorHelper";
import {
  BUSINESS_HOURS_DAY_KEYS,
  BusinessHourProps,
  BusinessHoursDay,
  BusinessHoursDays,
} from "./types";
import { TimeFormat, TimeOfDayValue } from "@/types/time";
import { formatTimeOfDayValueToTimeString } from "@/utils/timerHelper";
import "./styles.scss";

export function BusinessHours(props: BusinessHourProps) {
  const { defaultValue, onClick, ctaStyle } = props;

  const [timeFormat, setTimeFormat] = React.useState<TimeFormat>(defaultValue?.format || "AM/PM");
  const [days, setDays] = React.useState<BusinessHoursDays>(() => {
    const normalizeDay = (day?: BusinessHoursDay): BusinessHoursDay | undefined => {
      if (!day) return undefined;

      const enabled = Boolean(day.enabled);
      const times = Array.isArray(day.times)
        ? day.times.map((slot) => ({
            start: typeof slot?.start === "string" ? slot.start : "",
            end: typeof slot?.end === "string" ? slot.end : "",
          }))
        : [];

      return { enabled, times };
    };

    const incomingDays = defaultValue?.days;
    const emptyDays = getEmptyDays();
    if (!incomingDays) return emptyDays;

    if (Object.values(incomingDays).every((day) => day === undefined)) return emptyDays;

    const mergedDays = { ...emptyDays };
    BUSINESS_HOURS_DAY_KEYS.forEach((dayKey) => {
      const incomingDay = (
        incomingDays as Partial<Record<keyof BusinessHoursDays, BusinessHoursDay>>
      )[dayKey];
      const normalized = normalizeDay(incomingDay);
      if (normalized) mergedDays[dayKey] = normalized;
    });

    return mergedDays;
  });

  const onClickRef = React.useRef<BusinessHourProps["onClick"]>(onClick);
  React.useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  const handleTimeChange = (
    day: keyof BusinessHoursDays,
    timeIndex: number,
    timeSlot: "start" | "end",
    value?: TimeOfDayValue
  ) => {
    const formattedTime = formatTimeOfDayValueToTimeString(value) ?? "";

    setDays((previousDays) => {
      const dayData = previousDays[day];
      const times = dayData.times;

      if (!times[timeIndex]) return previousDays;

      const nextTimes = times.map((time, idx) =>
        idx === timeIndex ? { ...time, [timeSlot]: formattedTime } : time
      );

      return {
        ...previousDays,
        [day]: {
          ...dayData,
          times: nextTimes,
        },
      };
    });
  };

  const handleAddTimeSlot = (day: keyof BusinessHoursDays) => {
    setDays((previousDays) => {
      const dayData = previousDays[day];
      return {
        ...previousDays,
        [day]: {
          ...dayData,
          times: [...dayData.times, { start: "", end: "" }],
        },
      };
    });
  };

  const handleRemoveTimeSlot = (day: keyof BusinessHoursDays, timeIndex: number) => {
    setDays((previousDays) => {
      const dayData = previousDays[day];
      return {
        ...previousDays,
        [day]: {
          ...dayData,
          times: dayData.times.filter((_, idx) => idx !== timeIndex),
        },
      };
    });
  };

  const handleChangeEnabled = (day: keyof BusinessHoursDays, checked: boolean) => {
    setDays((previousDays) => {
      const dayData = previousDays[day];
      return {
        ...previousDays,
        [day]: {
          ...dayData,
          enabled: checked,
        },
      };
    });
  };

  React.useEffect(() => {
    onClickRef.current?.({ format: timeFormat, days });
  }, [days, timeFormat]);

  return (
    <BusinessHoursView
      ctaStyle={ctaStyle}
      timeFormat={timeFormat}
      days={days}
      onTimeFormatChange={setTimeFormat}
      onDayEnabledChange={handleChangeEnabled}
      onAddTimeSlot={handleAddTimeSlot}
      onRemoveTimeSlot={handleRemoveTimeSlot}
      onTimeChange={(day, timeIndex, timeSlot, timeValue?: TimeOfDayValue) =>
        handleTimeChange(day, timeIndex, timeSlot, timeValue)
      }
    />
  );
}
